import type { LandingPageContent } from '../types'

// GitLab-Issue #144: Übersetzung der deutschen How-to-Seite für Handwerksbetriebe, gleiche
// topicId für hreflang. Screenshots wie bei der ES-Kundenseite dieselben Bilddateien aus einer
// deutschsprachigen Test-Session - im Intro transparent gemacht.
export const COMO_FUNCIONA_PARA_INSTALADORES: LandingPageContent = {
  slug: 'como-funciona-para-instaladores',
  lang: 'es',
  topicId: 'so-funktioniert-es-fuer-handwerker',
  title: 'Cómo funciona Roofora para instaladores – Guía paso a paso',
  metaDescription:
    'Guía paso a paso para empresas instaladoras: crear una cuenta, consultar solicitudes ' +
    'anonimizadas, presentar un presupuesto gratuito y solicitar el contacto con el cliente.',
  keywords: [
    'Roofora guía instaladores',
    'Roofora cómo funciona para instaladores',
    'consultar solicitudes fotovoltaicas',
    'presentar presupuesto fotovoltaico instalador',
    'solicitar contacto con cliente solar',
  ],
  eyebrow: 'Guía para instaladores',
  h1: 'Cómo funciona Roofora para instaladores',
  intro:
    'Esta guía muestra cada paso desde la perspectiva de una empresa instaladora - desde el ' +
    'registro hasta presentar un presupuesto y solicitar el contacto - con capturas de pantalla ' +
    'reales de la aplicación. Las capturas provienen de una sesión de prueba en alemán; la ' +
    'aplicación está disponible también en español.',
  sections: [
    { type: 'heading', text: 'Explicado en 3 minutos' },
    {
      type: 'paragraph',
      text:
        'Registra una cuenta de empresa gratuita y consulta solicitudes solares anonimizadas de su ' +
        'zona - con datos del tejado, consumo y fotos, pero sin los datos de contacto del cliente. ' +
        'Presenta un presupuesto gratuito y sin compromiso, y al mismo tiempo solicita el contacto. ' +
        'Solo cuando el cliente autoriza específicamente a su empresa recibirá su correo electrónico ' +
        'y/o teléfono.',
    },
    { type: 'heading', text: '¿Qué necesito antes de empezar?' },
    {
      type: 'list',
      items: [
        'El nombre de su empresa',
        'No necesita correo electrónico ni teléfono, solo un nombre de usuario y una contraseña',
        'Para un presupuesto convincente: un precio aproximado y, opcionalmente, un presupuesto en PDF',
      ],
    },
  ],
  steps: [
    {
      title: 'Registro inicial',
      whatYouSee:
        'En la página de registro para instaladores, un formulario con nombre de empresa, nombre de ' +
        'usuario y contraseña, y un resumen breve: consultar solicitudes, presentar presupuesto, ' +
        'solicitar contacto.',
      whatToDo:
        'Haga clic en el botón para instaladores en la página principal, indique el nombre de su ' +
        'empresa, elija un nombre de usuario y una contraseña de al menos 8 caracteres, y complete el registro.',
      whatHappensNext: 'Al igual que con la cuenta de cliente, recibirá un código de recuperación - no hay reset de contraseña por correo.',
      successCriterion: 'Accede a su panel de instalador.',
      screenshot: {
        src: '/how-to/handwerker/01-registrierung.jpg',
        alt: 'Formulario de registro para instaladores con nombre de empresa, usuario y contraseña',
        width: 1120,
        height: 567,
      },
    },
    {
      title: 'Panel de instalador',
      whatYouSee:
        'El nombre de su empresa, su identificador y cuatro bloques - "Solicitudes abiertas" (con el ' +
        'número de solicitudes nuevas), "Mis solicitudes de contacto", "Lista de favoritos" y ' +
        '"Mercado B2B".',
      whatToDo: 'Haga clic en el bloque de solicitudes abiertas.',
      whatHappensNext: 'Se abre la lista anonimizada de todas las solicitudes solares abiertas.',
      successCriterion: 'Se muestra la vista "Solicitudes abiertas (anonimizadas)".',
      screenshot: {
        src: '/how-to/handwerker/02-dashboard.jpg',
        alt: 'Panel de instalador con los bloques Solicitudes abiertas, Mis solicitudes de contacto, Favoritos, Mercado B2B',
        width: 1120,
        height: 567,
      },
    },
    {
      title: 'Buscar entre las solicitudes abiertas',
      whatYouSee:
        'Una tabla con solicitudes anonimizadas - tipo de encargo, código postal, fechas, ' +
        'características de la instalación, número de presupuestos ya presentados y de ' +
        'autorizaciones. Encima, filtros por tipo de encargo, país, código postal e identificador.',
      whatToDo: 'Filtre por código postal o tipo de encargo para encontrar solicitudes de su zona y despliegue una fila con la flecha.',
      whatHappensNext: 'Solo verá el número de presupuestos ya presentados, no sus importes - cada empresa presenta su presupuesto de forma independiente.',
      successCriterion: 'Aparecen en la lista una o varias solicitudes que le interesan.',
      screenshot: {
        src: '/how-to/handwerker/03-offene-anfragen-liste.jpg',
        alt: 'Tabla de solicitudes abiertas y anonimizadas con filtros por código postal y tipo de encargo',
        width: 1120,
        height: 567,
      },
    },
    {
      title: 'Revisar los detalles de la solicitud',
      whatYouSee:
        'Al desplegar la fila, todos los datos relevantes en forma de etiquetas - tipo de vivienda, ' +
        'caras del tejado, tipo y material del tejado, año de construcción, cableado, ubicación del ' +
        'inversor/batería, personas en el hogar, consumo, preferencia de batería, deseo de contacto ' +
        'y franja horaria - además de las fotos del tejado y del cuadro eléctrico. Sin datos de ' +
        'contacto del cliente.',
      whatToDo: 'Compruebe si la solicitud encaja con su empresa (zona, tipo de encargo, estado del tejado en las fotos).',
      whatHappensNext: 'Si le interesa, baje hasta el formulario de presupuesto justo debajo de los detalles.',
      successCriterion: 'Tiene información suficiente para estimar un precio realista.',
      screenshot: {
        src: '/how-to/handwerker/04-anfrage-detail.jpg',
        alt: 'Detalles desplegados de la solicitud con etiquetas de vivienda, tejado, consumo y fotos subidas',
        width: 1120,
        height: 567,
      },
    },
    {
      title: 'Presentar presupuesto y solicitar contacto',
      whatYouSee:
        'Un campo para el precio estimado, una selección opcional de fabricante, un campo de texto ' +
        '"¿Por qué debería elegirle el cliente?" y la opción de adjuntar un presupuesto en PDF, con ' +
        'un botón para presentar el presupuesto y solicitar el contacto.',
      whatToDo:
        'Indique un precio estimado, añada opcionalmente un breve argumento diferenciador de su ' +
        'empresa y confirme el envío del presupuesto y la solicitud de contacto.',
      whatHappensNext:
        'Su presupuesto y su solicitud de contacto se envían en un mismo paso - ambos son gratuitos ' +
        'y sin compromiso. El cliente decide de forma individual si le autoriza el contacto.',
      successCriterion: 'Aparece una confirmación verde de que el presupuesto se ha enviado.',
      troubleshooting:
        'No incluya sus propios datos de contacto (correo, teléfono) en el campo de texto - esto ' +
        'provoca un error. Los presupuestos con documento PDF suelen recibir más autorizaciones.',
      screenshot: {
        src: '/how-to/handwerker/05-angebot-formular.jpg',
        alt: 'Formulario de presupuesto relleno con precio y texto de presentación, botón para enviar presupuesto y solicitar contacto',
        width: 1120,
        height: 567,
      },
    },
    {
      title: 'Confirmación y siguientes pasos',
      whatYouSee: 'El mensaje verde de confirmación sobre la lista de solicitudes (ahora vacía).',
      whatToDo:
        'Espere la decisión del cliente - en "Mis solicitudes de contacto" del panel puede ver el ' +
        'estado de todas sus solicitudes enviadas.',
      whatHappensNext: 'Si el cliente autoriza específicamente a su empresa, ahí recibirá su correo electrónico y/o teléfono, además de la franja horaria preferida.',
      successCriterion: 'El presupuesto aparece en "Mis solicitudes de contacto" con el estado correspondiente.',
      troubleshooting: 'Sin un correo electrónico registrado no recibirá avisos activos - revise su cuenta con regularidad.',
      screenshot: {
        src: '/how-to/handwerker/06-angebot-gesendet.jpg',
        alt: 'Mensaje de confirmación de presupuesto enviado en la lista de solicitudes',
        width: 1120,
        height: 567,
      },
    },
  ],
  faq: [
    {
      q: '¿Usar Roofora como instalador tiene algún coste?',
      a: 'No. Registro, consulta de solicitudes, presentación de presupuestos y solicitud de contacto son gratuitos actualmente.',
    },
    {
      q: '¿Veo los precios de otras empresas instaladoras?',
      a: 'No. Solo ve el número de presupuestos ya presentados, no su importe - cada empresa presenta su oferta de forma independiente.',
    },
    {
      q: '¿Recibo automáticamente los datos de contacto al presentar un presupuesto?',
      a: 'No. Al presentar el presupuesto solo solicita el contacto. Recibirá los datos únicamente cuando el cliente autorice específicamente a su empresa.',
    },
    {
      q: '¿Puedo modificar mi presupuesto más adelante?',
      a: 'Sí, mientras la solicitud siga abierta puede ajustar o mejorar su presupuesto en cualquier momento.',
    },
    {
      q: '¿Qué pasa si el cliente no autoriza mi solicitud de contacto?',
      a: 'No se genera ningún coste ni obligación - puede seguir consultando otras solicitudes de su zona.',
    },
  ],
  primaryCta: { label: 'Registrarse gratis como instalador', action: 'craftsman' },
  secondaryCta: { label: 'Ver la guía para clientes', action: 'link', href: '/como-funciona-para-clientes' },
  relatedLinks: [
    { label: 'Para instaladores: encontrar más encargos', href: '/encargos-fotovoltaica-instaladores' },
    { label: 'Cómo funciona Roofora para clientes', href: '/como-funciona-para-clientes' },
  ],
}
